import { MuiButton } from '@/components/button/MuiButton';
import {
  formatPublicKeyCredential,
  formatPublicKeyCredentialRequestOptions,
} from '@/helpers/webAuthnHelper';
import { ibowClient } from '@/init/ibowClient';
import { useSnackbar } from '@/hooks/useSnackbar';
import { setToken } from '@/helpers/apiRequestHelper';
import { useSearchParams } from 'next/navigation';
import { createPortalSession } from '@/helpers/accountHelper';
import { PORTAL_USE_TYPE } from '@/types/types';
import { Session } from 'next-auth';

type Props = {
  useType: PORTAL_USE_TYPE;
  loginInfo: {
    token: string;
    ewellUserId: number;
  };
  session: Session | null;
  setIsLoading: (value: boolean) => void;
  handleNotRegistered: () => void;
};

export function LoginWithWebAuthn({
  useType,
  loginInfo,
  session,
  setIsLoading,
  handleNotRegistered,
}: Props) {
  const { openSnackbar, closeSnackbar } = useSnackbar();
  const searchParams = useSearchParams();

  // 生体認証でログインする
  const loginWithWebAuthn = async () => {
    setIsLoading(true);
    closeSnackbar();

    try {
      // 生体認証に必要な情報をサーバーから取得
      const optionsData = await ibowClient.webAuthn
        .assertionBegin({
          body: { ewell_user_id: loginInfo.ewellUserId },
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          if (error.body.code == 101) {
            // 生体認証が未登録の場合
            handleNotRegistered();
          }
          throw error;
        });

      // デバイスに渡すためのデータ整形
      const options = formatPublicKeyCredentialRequestOptions(
        optionsData.webauthn_response,
      );

      // デバイスでの認証を行う
      const credential = await navigator.credentials.get({
        publicKey: options,
      });
      if (!credential) throw new Error('デバイスで認証ができませんでした');

      // サーバーに渡すためのデータ整形
      const assertion = formatPublicKeyCredential(credential);

      // サーバーに生体認証の結果を送信
      const response = await ibowClient.webAuthn.assertionComplete({
        body: {
          assertion,
          ewell_user_id: loginInfo.ewellUserId,
          token: loginInfo.token,
        },
      });

      if (response.token && response.ref_token) {
        // ログイン成功
        await setToken({ token: response.token, refToken: response.ref_token });
        const params = new URLSearchParams(searchParams.toString());
        params.set('use_type', useType.toString());
        await createPortalSession(
          searchParams,
          session,
          useType,
          `/recept?${params.toString()}`,
        );
      }
    } catch {
      setIsLoading(false);
      openSnackbar('生体認証に失敗しました', 'error');
    }

    setIsLoading(false);
  };

  return (
    <>
      <MuiButton
        type="button"
        size="large"
        color="inherit"
        onClick={loginWithWebAuthn}
      >
        生体認証を使用してログインする
      </MuiButton>
    </>
  );
}
